import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  ListHuntsResponse,
  GetPlayStateResponse,
  SubmitAnswerResponse,
  GetLeaderboardResponse,
  HuntStats,
  JoinHuntResponse,
  GetMyHuntsResponse,
} from '@cloudfruit-hunts/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
    }
  } catch {
    // Not authenticated
  }
  return { 'Content-Type': 'application/json' };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

// Public endpoints
export async function listHunts(): Promise<ListHuntsResponse> {
  const response = await fetch(`${API_URL}/hunts`);
  return handleResponse(response);
}

export async function getHuntStats(huntId: string): Promise<HuntStats> {
  const response = await fetch(`${API_URL}/hunts/${huntId}/stats`);
  const data = await handleResponse<{ stats: HuntStats }>(response);
  return data.stats;
}

export async function getLeaderboard(huntId: string): Promise<GetLeaderboardResponse> {
  const response = await fetch(`${API_URL}/hunts/${huntId}/leaderboard`);
  return handleResponse(response);
}

// Authenticated endpoints
export async function getPlayState(huntId: string): Promise<GetPlayStateResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/hunts/${huntId}/play`, { headers });
  return handleResponse(response);
}

export async function submitAnswer(huntId: string, answer: string): Promise<SubmitAnswerResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/hunts/${huntId}/submit`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ answer }),
  });
  return handleResponse(response);
}

export async function sendPresence(huntId: string): Promise<{ activePlayers: number }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/hunts/${huntId}/presence`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
}

export async function joinHunt(huntId: string): Promise<JoinHuntResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/hunts/${huntId}/join`, {
    method: 'POST',
    headers,
  });
  return handleResponse(response);
}

export async function getMyHunts(): Promise<GetMyHuntsResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/me/hunts`, { headers });
  return handleResponse(response);
}

// Admin endpoints
export async function playtest(
  huntId: string,
  action: 'start' | 'reset'
): Promise<{ success: boolean; message: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/admin/playtest`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ huntId, action }),
  });
  return handleResponse(response);
}
